import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useContractRead, usePublicClient } from 'wagmi';
import { CONTRACT_ADDRESSES } from '@/config/addresses';
import { factoryV5ABI } from '@/config/abi/factoryV5';
import { formatEther } from 'viem';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle, BarChart3, Users as UsersIcon, RefreshCw } from 'lucide-react';
import EnhancedAnalytics from '@/components/admin/EnhancedAnalytics';
import { EnhancedBlockchainAnalytics } from '@/components/admin/EnhancedBlockchainAnalytics';
import { auth } from '@/lib/firebase';
import { AdminLogin } from '@/components/admin/AdminLogin';

interface TokenCreationEvent {
  args?: {
    tokenAddress: string;
    owner: string;
    name: string;
    symbol: string;
  };
  blockNumber: bigint;
  transactionHash: string;
}

export function Analytics() {
  const publicClient = usePublicClient();
  const [activeUsers, setActiveUsers] = useState<number>(0);
  const [platformRevenue, setPlatformRevenue] = useState<string>("0");
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [totalTokensAllChains, setTotalTokensAllChains] = useState<number>(0);
  const [isLoadingTokenCount, setIsLoadingTokenCount] = useState<boolean>(true);
  
  // Visitor analytics state
  const [analyticsTab, setAnalyticsTab] = useState<'blockchain' | 'visitors'>('blockchain');
  const [visitorAnalyticsData, setVisitorAnalyticsData] = useState<any>(null);
  const [visitorAnalyticsLoading, setVisitorAnalyticsLoading] = useState(false);

  // Get total tokens created from BSC Testnet (for backward compatibility)
  const { data: tokensCreated, refetch: refetchTokensCreated } = useContractRead({
    address: CONTRACT_ADDRESSES[97].factory,
    abi: factoryV5ABI,
    functionName: 'totalTokensCreated',
    watch: true,
    chainId: 97
  });

  // Get platform fee from BSC Testnet
  const { data: platformFee } = useContractRead({
    address: CONTRACT_ADDRESSES[97].factory,
    abi: factoryV5ABI,
    functionName: 'platformFee',
    watch: true,
    chainId: 97
  });

  // Fetch visitor analytics from Firebase
  const fetchVisitorAnalytics = async () => {
    if (!auth?.currentUser) return;
    
    setVisitorAnalyticsLoading(true);
    try {
      const token = await auth.currentUser.getIdToken();
      // Always use production URL for Netlify functions (Android app compatibility)
      const response = await fetch('https://mdbapp.io/.netlify/functions/admin-analytics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVisitorAnalyticsData(data);
      } else {
        console.error('Failed to fetch visitor analytics');
      }
    } catch (error) {
      console.error('Error fetching visitor analytics:', error);
    } finally {
      setVisitorAnalyticsLoading(false);
    }
  };

  // Auto-load visitor analytics on mount if user is logged in
  useEffect(() => {
    if (auth?.currentUser && !visitorAnalyticsData) {
      fetchVisitorAnalytics();
    }
  }, [auth?.currentUser]);

  // Fetch total tokens from all supported chains
  useEffect(() => {
    const fetchTotalTokensAllChains = async () => {
      setIsLoadingTokenCount(true);
      try {
        // All supported chain IDs with factories
        const chainIds = [
          97,       // BSC Testnet
          56,       // BSC Mainnet
          1,        // Ethereum Mainnet
          11155111, // Sepolia
          43113,    // Avalanche Fuji
          43114,    // Avalanche Mainnet
          11124,    // Abstract Testnet
          2741,     // Abstract Mainnet
          8453,     // Base Mainnet
          84532,    // Base Sepolia
          10,       // OP Mainnet
          11155420, // OP Sepolia
          42161,    // Arbitrum One
          421614,   // Arbitrum Sepolia
          137,      // Polygon Mainnet
          80002,    // Polygon Amoy
          1328,     // Sei Testnet
          1329,     // Sei Mainnet
          204,      // opBNB Mainnet
          5611      // opBNB Testnet
        ];

        // Fetch all chains in parallel with timeout
        const fetchWithTimeout = async (chainId: number) => {
          const factoryAddress = CONTRACT_ADDRESSES[chainId as keyof typeof CONTRACT_ADDRESSES]?.factory;
          if (!factoryAddress) return 0;

          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout per chain

            const response = await fetch(`/.netlify/functions/get-token-count`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ chainId }),
              signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (response.ok) {
              const data = await response.json();
              return data.count || 0;
            }
            return 0;
          } catch (error) {
            console.error(`Error fetching token count for chain ${chainId}:`, error);
            return 0;
          }
        };

        // Fetch all chains in parallel
        const results = await Promise.all(chainIds.map(fetchWithTimeout));
        const total = results.reduce((sum, count) => sum + count, 0);

        setTotalTokensAllChains(total);
        console.log(`Total tokens across all chains: ${total}`);
      } catch (error) {
        console.error('Error fetching total tokens:', error);
      } finally {
        setIsLoadingTokenCount(false);
      }
    };

    fetchTotalTokensAllChains();
  }, [tokensCreated, refreshTrigger]);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        if (!publicClient) return;

        console.log('Fetching blockchain analytics...');

        // Get current block
        const currentBlock = await publicClient.getBlockNumber();
        
        // Calculate block ranges (50,000 blocks per request, approximately 7 days)
        const blocksPerDay = BigInt(20 * 60 * 24); // ~20 blocks per minute
        const thirtyDaysBlocks = blocksPerDay * BigInt(30);
        const fromBlock = currentBlock - thirtyDaysBlocks;
        
        // Split into multiple requests to stay within block range limit
        const batchSize = BigInt(50000);
        const requests = [];
        let startBlock = fromBlock;
        
        while (startBlock < currentBlock) {
          const endBlock = startBlock + batchSize > currentBlock ? currentBlock : startBlock + batchSize;
          requests.push(
            publicClient.getContractEvents({
              address: CONTRACT_ADDRESSES[97].factory,
              abi: factoryV5ABI,
              eventName: 'TokenDeployed',
              fromBlock: startBlock,
              toBlock: endBlock
            })
          );
          startBlock += batchSize;
        }

        // Get all events
        const eventBatches = await Promise.all(requests);
        const events = eventBatches.flat();

        console.log(`Found ${events.length} token creation events`);

        // Calculate unique users
        const uniqueUsers = new Set(
          events
            .filter(event => (event as any).args?.owner)
            .map(event => (event as any).args.owner)
        );
        setActiveUsers(uniqueUsers.size);

        // Calculate platform revenue
        if (platformFee && typeof platformFee === 'bigint') {
          const revenue = BigInt(events.length) * platformFee;
          setPlatformRevenue(formatEther(revenue));
        }

        console.log('Analytics updated:', {
          activeUsers: uniqueUsers.size,
          platformRevenue: platformFee ? formatEther(BigInt(events.length) * platformFee) : '0'
        });
      } catch (error) {
        console.error('Error fetching analytics:', error);
      }
    };

    fetchAnalytics();
  }, [publicClient, platformFee, tokensCreated, refreshTrigger]);

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Admin Login */}
        <div className="mb-4 flex justify-center">
          <AdminLogin />
        </div>

        {/* Tab Buttons */}
        <div className="flex gap-2 justify-center items-center flex-wrap">
          <Button
            variant={analyticsTab === 'blockchain' ? 'default' : 'outline'}
            onClick={() => setAnalyticsTab('blockchain')}
            className="flex items-center gap-2"
          >
            <BarChart3 className="h-4 w-4" />
            Blockchain
          </Button>
          <Button
            variant={analyticsTab === 'visitors' ? 'default' : 'outline'}
            onClick={() => {
              setAnalyticsTab('visitors');
              if (!visitorAnalyticsData) {
                fetchVisitorAnalytics();
              }
            }}
            className="flex items-center gap-2"
          >
            <UsersIcon className="h-4 w-4" />
            Visitors
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (analyticsTab === 'blockchain') {
                setRefreshTrigger(prev => prev + 1);
                refetchTokensCreated();
              } else {
                fetchVisitorAnalytics();
              }
            }}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Blockchain Analytics Tab */}
        {analyticsTab === 'blockchain' && (
          <EnhancedBlockchainAnalytics />
        )}

        {/* Visitor Analytics Tab */}
        {analyticsTab === 'visitors' && (
          <EnhancedAnalytics
            analyticsData={visitorAnalyticsData}
            onRefresh={fetchVisitorAnalytics}
            loading={visitorAnalyticsLoading}
          />
        )}
      </div>
    </TooltipProvider>
  );
}
